import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, UserIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { BackendUser, NewUser, UpdateUser } from '@/types';
import { usersApi } from '@/services/api/users';
import { usePermissions, usePermissionGuard, useOnPermissionChange } from '@/hooks';
import { AxiosError } from 'axios';
import type { ApiErrorResponse } from '@/types';
import {
  PageToolbar,
  ToolbarIcons,
  ButtonConfig,
  ListCard,
  ConfigCard,
  ModalCard,
  StatsCard,
  type ListItem,
  type ConfigField,
} from '../../components/ui';

interface UserModalData {
  user_id?: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  company: string;
  enabled?: boolean;
}

const Users: React.FC = () => {
  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Modal states
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userModalData, setUserModalData] = useState<UserModalData>({
    first_name: '',
    last_name: '',
    email: '',
    role: 'user',
    company: 'company',
  });

  const queryClient = useQueryClient();

  // Permission checks
  const permissions = usePermissions();

  // Guard this page - redirect if user loses permission
  const { hasPermission, isChecking } = usePermissionGuard({
    requiredPermission: () => permissions.canManageUsers(),
    redirectTo: '/unauthorized',
    onPermissionLost: () => {
      // Close any open modals when permission is lost
      setIsUserModalOpen(false);
    },
  });

  // React to permission changes by refetching data
  useOnPermissionChange(() => {
    if (permissions.canManageUsers()) {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on search
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // API Queries
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users', currentPage, debouncedSearchTerm],
    queryFn: () =>
      usersApi.getUsers({
        page: currentPage,
        size: 20,
        search: debouncedSearchTerm || undefined,
      }),
    enabled: permissions.canManageUsers(),
    staleTime: 5 * 60 * 1000,
  });

  // Mutations
  const createUserMutation = useMutation({
    mutationFn: usersApi.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeUserModal();
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      alert(`Failed to create user: ${error.response?.data?.error || error.message}`);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: usersApi.updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeUserModal();
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      alert(`Failed to update user: ${error.response?.data?.error || error.message}`);
    },
  });

  // Modal Handlers
  const openNewUserModal = () => {
    setUserModalData({
      first_name: '',
      last_name: '',
      email: '',
      role: 'user',
      company: 'company',
      enabled: true,
    });
    setIsUserModalOpen(true);
  };

  const openEditUserModal = (user: BackendUser) => {
    setUserModalData({
      user_id: user.user_id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      role: user.role || 'user',
      company: user.company || 'company',
      enabled: user.enabled !== false,
    });
    setIsUserModalOpen(true);
  };

  const closeUserModal = () => {
    setIsUserModalOpen(false);
    setUserModalData({
      first_name: '',
      last_name: '',
      email: '',
      role: 'user',
      company: 'company',
    });
  };

  const submitUserModal = async () => {
    if (userModalData.user_id) {
      // Update existing user
      const updateData: UpdateUser = {
        user_id: userModalData.user_id,
        first_name: userModalData.first_name,
        last_name: userModalData.last_name,
        email: userModalData.email,
        role: userModalData.role,
        company: userModalData.company,
        enabled: userModalData.enabled,
      };
      updateUserMutation.mutate(updateData);
    } else {
      // Create new user
      const newData: NewUser = {
        first_name: userModalData.first_name,
        last_name: userModalData.last_name,
        email: userModalData.email,
        role: userModalData.role,
        company: userModalData.company,
      };
      createUserMutation.mutate(newData);
    }
  };

  // Pagination handlers
  const nextPage = () => {
    if (usersData?.has_more) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Handle field changes
  const handleUserFieldChange = (fieldId: string, value: string | number) => {
    if (fieldId === 'enabled') {
      // Convert string to boolean for enabled field
      setUserModalData((prev) => ({
        ...prev,
        enabled: value === 'true',
      }));
    } else {
      setUserModalData((prev) => ({
        ...prev,
        [fieldId]: value,
      }));
    }
  };

  // Convert users to ListItem format
  const userListItems: ListItem[] =
    usersData?.users.map((user) => ({
      id: user.user_id,
      primary: `${user.first_name} ${user.last_name}`,
      secondary: user.email,
      tertiary: `${user.role || 'user'} • ${user.company || 'company'}${user.enabled === false ? ' • disabled' : ''}`,
      icon: <UserIcon />,
      actions: (
        <button
          onClick={(e) => {
            e.stopPropagation();
            openEditUserModal(user);
          }}
          className="p-1 text-gray-400 hover:text-onyx-bright-blue transition-colors"
          title="Edit User"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
      ),
    })) || [];

  // User configuration fields
  const userConfigFields: ConfigField[] = [
    {
      id: 'first_name',
      label: 'First Name',
      type: 'text',
      value: userModalData.first_name,
      placeholder: 'Enter first name',
      required: true,
    },
    {
      id: 'last_name',
      label: 'Last Name',
      type: 'text',
      value: userModalData.last_name,
      placeholder: 'Enter last name',
      required: true,
    },
    {
      id: 'email',
      label: 'Email',
      type: 'email',
      value: userModalData.email,
      placeholder: 'Enter email address',
      required: true,
      disabled: !!userModalData.user_id, // Can't change email for existing users
    },
    {
      id: 'role',
      label: 'Role',
      type: 'select',
      value: userModalData.role,
      options: [
        { value: 'user', label: 'User' },
        { value: 'admin', label: 'Admin' },
      ],
      required: true,
    },
    {
      id: 'company',
      label: 'Company',
      type: 'select',
      value: userModalData.company,
      options: [
        { value: 'customer', label: 'External Company' },
        { value: 'onyx', label: 'ONYX' },
      ],
      required: true,
    },
    {
      id: 'enabled',
      label: 'Account Status',
      type: 'select',
      value: userModalData.enabled ? 'true' : 'false',
      options: [
        { value: 'true', label: 'Enabled' },
        { value: 'false', label: 'Disabled' },
      ],
      required: true,
    },
  ];

  // Stats calculations
  const totalUsers = usersData?.total_count || 0;
  const adminUsers = usersData?.users.filter((u) => u.role === 'admin').length || 0;
  const onyxUsers = usersData?.users.filter((u) => u.company === 'onyx').length || 0;
  const customerUsers = usersData?.users.filter((u) => u.company === 'customer').length || 0;
  const disabledUsers = usersData?.users.filter((u) => u.enabled === false).length || 0;

  // Check permissions using the guard
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-onyx-bright-blue mx-auto" />
          <p className="mt-4 text-sm text-gray-500">Checking permissions...</p>
        </div>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            You don&apos;t have permission to manage users.
          </p>
        </div>
      </div>
    );
  }

  // Configure toolbar buttons (minimal - no add button)
  const toolbarButtons: ButtonConfig[] = [];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageToolbar
        title="User Management"
        icon={ToolbarIcons.AddUser}
        buttons={toolbarButtons}
      />

      <div className="max-w-7xl mx-auto p-6">
        <div className='space-y-6'>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <StatsCard
              title="Total Users"
              value={totalUsers}
              description="Active and disabled"
              color="bright-blue"
              loading={usersLoading}
            />

            <StatsCard
              title="Admin Users"
              value={adminUsers}
              description="With admin privileges"
              color="purple"
              loading={usersLoading}
            />

            <StatsCard
              title="Internal Users"
              value={customerUsers}
              description={`with ${onyxUsers} ONYX users`}
              color="green"
              loading={usersLoading}
            />

            <StatsCard
              title="Disabled"
              value={disabledUsers}
              description="Inactive accounts"
              color="dark-blue"
              loading={usersLoading}
            />
          </div>


          {/* Search Bar */}
          <div>
            <div className="relative max-w-md">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users by name or email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-onyx-bright-blue"
              />
            </div>
          </div>
        </div>

        {/* Users List */}
        <ListCard
          title="Users"
          subtitle={usersLoading ? 'Loading...' : `${usersData?.total_count || 0} users total`}
          items={userListItems}
          headerActions={
            <button
              onClick={openNewUserModal}
              className="px-3 py-1.5 bg-onyx-purple text-white onyx-button font-fk-medium rounded-lg hover:bg-onyx-dark-blue transition-colors text-sm"
            >
              <PlusIcon className="h-4 w-4 inline mr-1" />
              Add User
            </button>
          }
          footerActions={
            usersData && usersData.total_pages > 1 ? (
              <div className="flex items-center justify-between w-full">
                <p className="text-sm text-gray-700 onyx-body">
                  Page {currentPage} of {usersData.total_pages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={prevPage}
                    disabled={currentPage <= 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={nextPage}
                    disabled={!usersData.has_more}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : undefined
          }
          loading={usersLoading}
          emptyState={{
            icon: <UserIcon />,
            title: searchTerm ? 'No users found' : 'No users yet',
            description: searchTerm
              ? 'Try adjusting your search criteria'
              : 'Get started by creating the first user account',
            action: !searchTerm ? (
              <button
                onClick={openNewUserModal}
                className="px-4 py-2 bg-onyx-purple text-white onyx-button font-fk-medium rounded-lg hover:bg-onyx-dark-blue transition-colors"
              >
                <PlusIcon className="h-4 w-4 inline mr-2" />
                Add First User
              </button>
            ) : undefined,
          }}
        />

        {/* User Configuration Modal */}
        <ModalCard
          isOpen={isUserModalOpen}
          onClose={closeUserModal}
          title={userModalData.user_id ? 'Edit User' : 'Add New User'}
          subtitle="Configure user account details and permissions"
          size="md"
        >
          <ConfigCard
            fields={userConfigFields}
            onFieldChange={handleUserFieldChange}
            onSubmit={submitUserModal}
            onCancel={closeUserModal}
            submitLabel={userModalData.user_id ? 'Update User' : 'Create User'}
            isSubmitting={createUserMutation.isPending || updateUserMutation.isPending}
          />
        </ModalCard>
      </div>
    </div>
  );
};

export default Users;
